import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, Volume2, VolumeX, Eye } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BackgroundPreviewSlideshow = ({ backgrounds = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [previousIndex, setPreviousIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const videoRefs = useRef({});

  // Reset when backgrounds change
  useEffect(() => {
    setCurrentIndex(0);
    setPreviousIndex(-1);
    setIsPlaying(false);
  }, [backgrounds.length]);

  // Slideshow timer
  useEffect(() => {
    if (!isPlaying || backgrounds.length <= 1) return;
    const current = backgrounds[currentIndex];
    const duration = (current?.duration || 10) * 1000;

    const timer = setTimeout(() => {
      setPreviousIndex(currentIndex);
      setCurrentIndex((currentIndex + 1) % backgrounds.length);
    }, duration);

    return () => clearTimeout(timer);
  }, [currentIndex, backgrounds, isPlaying]);

  // Clear previous after cross-fade
  useEffect(() => {
    if (previousIndex === -1) return;
    const timer = setTimeout(() => setPreviousIndex(-1), 2200);
    return () => clearTimeout(timer);
  }, [previousIndex]);

  // Video play/pause management
  useEffect(() => {
    Object.entries(videoRefs.current).forEach(([idx, el]) => {
      if (!el) return;
      const i = parseInt(idx);
      if (i === currentIndex && isPlaying) {
        el.play().catch(() => {});
      } else if (i === previousIndex) {
        // keep playing during fade
      } else {
        el.pause();
      }
    });
  }, [currentIndex, previousIndex, isPlaying]);

  if (backgrounds.length === 0) return null;

  return (
    <div className="bg-slate-800/50 border border-blue-500/20 rounded-xl overflow-hidden" data-testid="bg-preview-slideshow">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-blue-500/10">
        <span className="text-sm font-medium text-blue-300/80 flex items-center gap-2">
          <Eye className="w-4 h-4" /> Apercu du fondu enchaine
        </span>
        <div className="flex items-center gap-2">
          {backgrounds.some(bg => bg.type === 'video') && (
            <button
              onClick={() => {
                setIsMuted(prev => {
                  const next = !prev;
                  Object.values(videoRefs.current).forEach(v => { if (v) v.muted = next; });
                  return next;
                });
              }}
              className="p-1.5 rounded-lg text-blue-300/50 hover:text-blue-300 hover:bg-blue-500/10 transition-colors"
              data-testid="preview-mute-toggle"
            >
              {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
          )}
          <button
            onClick={() => setIsPlaying(p => !p)}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium transition-all ${
              isPlaying
                ? 'bg-blue-500/20 text-blue-300 border border-blue-500/40'
                : 'bg-slate-700/50 text-blue-300/60 border border-blue-500/10 hover:border-blue-500/30'
            }`}
            data-testid="preview-play-toggle"
          >
            {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            {isPlaying ? 'Pause' : 'Lancer'}
          </button>
        </div>
      </div>

      {/* Slideshow area */}
      <div className="relative w-full aspect-video bg-black overflow-hidden">
        {backgrounds.map((bg, i) => {
          const isActive = i === currentIndex;
          const isPrevious = i === previousIndex;
          const url = bg.url?.startsWith('http') ? bg.url : `${BACKEND_URL}${bg.url}`;
          return (
            <div
              key={bg.id || i}
              style={{
                position: 'absolute',
                inset: 0,
                opacity: isActive ? 1 : 0,
                transition: (isActive || isPrevious) ? 'opacity 2s ease-in-out' : 'none',
                zIndex: isActive ? 2 : isPrevious ? 1 : 0,
                willChange: (isActive || isPrevious) ? 'opacity' : 'auto',
                transform: 'translateZ(0)',
                visibility: (isActive || isPrevious) ? 'visible' : 'hidden',
              }}
            >
              {bg.type === 'video' ? (
                <video
                  src={url}
                  muted={isMuted}
                  loop
                  playsInline
                  className="w-full h-full object-cover"
                  ref={el => { videoRefs.current[i] = el; }}
                />
              ) : (
                <img src={url} className="w-full h-full object-cover" alt={`Fond ${i + 1}`} />
              )}
            </div>
          );
        })}

        {/* Overlay with info */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10 pointer-events-none" />
        <div className="absolute bottom-3 left-3 z-10 pointer-events-none">
          <span className="text-xs text-white/80 bg-black/50 backdrop-blur-sm px-2 py-1 rounded-full">
            Fond {currentIndex + 1}/{backgrounds.length}
            {backgrounds[currentIndex]?.duration && ` — ${backgrounds[currentIndex].duration}s`}
          </span>
        </div>

        {/* Dots indicator */}
        {backgrounds.length > 1 && (
          <div className="absolute bottom-3 right-3 z-10 flex gap-1.5">
            {backgrounds.map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  setPreviousIndex(currentIndex);
                  setCurrentIndex(i);
                }}
                className={`w-2 h-2 rounded-full transition-all ${i === currentIndex ? 'bg-white w-5' : 'bg-white/40 hover:bg-white/60'}`}
                data-testid={`preview-dot-${i}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BackgroundPreviewSlideshow;
